import useSuccessToast from "@configs/react-toastify/snackbar";
import queryKeys from "@configs/react-query/query-key";
import { TOAST } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import {
  buildCreateProjectBody,
  buildInitialAddProjectFormData,
  getAssetClassificationDefault,
  getCompletionRequestDaysDefault,
  getDistributionScopeDefault,
  getLicensePolicyDefault,
  isAddProjectFormValid,
} from "@utils/addProjectForm";
import { normalizeResponseData } from "@utils/common";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

export const useAddProjectForm = ({
  productSeq = "",
  isActive = true,
  resetOnActivate = false,
  resetOnCreateCompleted = false,
  onCreateCompleted,
} = {}) => {
  const { t } = useTranslation();
  const { canAddProjectMemberPermission } = useContext(PermissionContext);
  const hasInitializedRef = useRef(false);
  const normalizedProductSeq =
    productSeq === null || productSeq === undefined || productSeq === ""
      ? ""
      : Number.isNaN(Number(productSeq))
        ? productSeq
        : Number(productSeq);

  const [modalSelectLicenseOpen, setModalSelectLicenseOpen] = useState(false);
  const [distributionScopeList, setDistributionScopeList] = useState([]);
  const [licensePolicyList, setLicensePolicyList] = useState([]);
  const [defaultLicense, setDefaultLicense] = useState(null);
  const [formData, setFormData] = useState({
    projectName: "",
    productSeq: normalizedProductSeq,
    completionRequestDate: "",
    assetClassification: "",
    distributionScope: "",
    licensePolicy: "",
    license: "",
    selectedLicense: null,
    selectedProjectPreview: null,
    selectedMembers: [],
    projectManagerId: "",
    remark: "",
    selectedFiles: [],
  });
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [modalSelectMemberOpen, setModalSelectMemberOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(true);
  const [creatorOptions, setCreatorOptions] = useState([]);
  const [myProductsList, setMyProductsList] = useState([]);

  const { assetClassificationsList, creatorList, departmentList, productsList, projectManagerList, fetchSearchInfo } =
    useSearchInfo();
  const { uploadFilesWithCustomCallback } = useFileGroupAPI(0);

  const clientConfigContext = useClientConfig();
  const clientConfig = clientConfigContext?.clientConfig || {};
  const clientConfigLoading = clientConfigContext?.isLoading || false;
  const getClientConfigValue = clientConfigContext?.getClientConfigValue || (() => null);

  const getInitialFormData = () =>
    buildInitialAddProjectFormData({
      productSeq: normalizedProductSeq,
      assetClassificationsList,
      distributionScopeList,
      licensePolicyList,
      clientConfig,
      defaultLicense,
    });

  const resetFormState = () => {
    setFormData(getInitialFormData());
    setIsPreviewModalOpen(false);
    setModalSelectLicenseOpen(false);
    setModalSelectMemberOpen(false);
    setLoading(false);
  };

  useEffect(() => {
    if (clientConfigContext && !clientConfigLoading && clientConfig && Object.keys(clientConfig).length > 0) {
      try {
        const usePreview = getClientConfigValue("usePreview");
        if (usePreview !== null && usePreview !== undefined) {
          setIsPreviewEnabled(usePreview === "Y");
        }
      } catch (error) {
        console.error("Error getting preference value:", error);
        setIsPreviewEnabled(true);
      }
    }
  }, [clientConfigContext, clientConfig, clientConfigLoading, getClientConfigValue]);

  const getLicensePolicyList = useMutationData(
    queryKeys.licensePoliciesOnly,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setLicensePolicyList(
            normalizeResponseData(response).map(({ licensePolicySeq, policyName }) => ({
              value: licensePolicySeq,
              label: policyName,
            })),
          );
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const getDistributionScopes = useMutationData(
    queryKeys.getDistributionScopes,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setDistributionScopeList(response.map((data) => ({ value: data.code, label: data.codeName })));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const getLicenseDefault = useMutationData(
    queryKeys.getListLicenseAttributes,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const license = response.find((item) => item.defaultFlag === "Y");
          if (license) {
            const mappedDefaultLicense = {
              licenseIdentifier: license.licenseIdentifier,
              licenseName: license.licenseName,
              defaultFlag: license.defaultFlag,
            };
            setDefaultLicense(mappedDefaultLicense);
            setFormData((prev) => (
              prev.selectedLicense
                ? prev
                : {
                    ...prev,
                    selectedLicense: mappedDefaultLicense,
                  }
            ));
          }
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const getSearchCreators = useMutationData(
    queryKeys.getSearchCreatorList,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (Array.isArray(response)) {
          const mappedOptions = response.map((item) => ({
            ...item,
            value: item.createrId,
            label: item.createrName,
          }));
          setCreatorOptions(mappedOptions);
        }
      },
      onError: (error) => {
        console.error("Error fetching search info data:", error);
      },
    },
  );

  const mapMyProductsToOptions = (items) =>
    (items ?? []).map((item) => ({
      value: item.productSeq,
      label: item.productName,
    }));

  const extractMyProductsFromResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.object)) return response.object;
    return [];
  };

  const getMyProducts = useMutationData(
    queryKeys.myProducts,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setMyProductsList(mapMyProductsToOptions(extractMyProductsFromResponse(response)));
        }
      },
      onError: (error) => {
        console.error("Error fetching my products:", error);
      },
    },
  );

  const finalizeCreate = () => {
    if (resetOnCreateCompleted) {
      resetFormState();
    } else {
      setLoading(false);
    }
    if (onCreateCompleted) {
      onCreateCompleted();
    }
  };

  const createProject = useMutationData(
    queryKeys.createProject,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        useSuccessToast(TOAST.success, t("project.project-created-success"), t("project.project-created-description"));
        await updateDataInIndexedDb(STORES.PROJECTS, response, response.projectSeq);

        const validFiles = formData.selectedFiles.filter((file) => file.fileInfoSeq || file.file);
        const newFiles = validFiles.map((file) => file.file).filter(Boolean);
        const uploadFormData = new FormData();
        uploadFormData.append("targetSeq", response.projectSeq);
        uploadFormData.append("targetTypeCd", "PROJECT");
        uploadFormData.append("fileGroupNo", response.fileGroupNo ? response.fileGroupNo : 0);

        if (newFiles?.length > 0) {
          const uploadFilesData = newFiles.reduce((acc, file, index) => {
            if (file instanceof File) {
              acc[`file_${index}`] = file;
            }
            return acc;
          }, {});

          Object.entries(uploadFilesData).forEach(([key, file]) => {
            uploadFormData.append(key, file, file.name);
          });
        }

        uploadFilesWithCustomCallback.mutate(uploadFormData, {
          onSuccess: () => {
            if (newFiles?.length > 0) {
              useSuccessToast(TOAST.success, t("common.files-upload-success"), t("common.files-upload-description"));
            }
            finalizeCreate();
          },
          onError: (error) => {
            console.error("Error during upload data:", error);
            if (newFiles?.length > 0) {
              useSuccessToast(TOAST.error, t("common.files-upload-error"), t("common.files-upload-description"));
            }
            finalizeCreate();
          },
        });

        if (newFiles?.length === 0) {
          finalizeCreate();
        }
      },
      onError: (error) => {
        console.error("Error during create project:", error);
        toast.error(t("project.project-add-error", "Failed to create project"), {
          autoClose: 1000,
        });
        setLoading(false);
      },
    },
  );

  useEffect(() => {
    if (!isActive) return;

    if (resetOnActivate) {
      resetFormState();
    }

    if (!hasInitializedRef.current) {
      getLicenseDefault.mutate();
      getDistributionScopes.mutateAsync();
      getLicensePolicyList.mutateAsync();
      getSearchCreators.mutateAsync();
      getMyProducts.mutateAsync();
      fetchSearchInfo();
      hasInitializedRef.current = true;
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive && normalizedProductSeq !== "") {
      setFormData((prev) => ({
        ...prev,
        productSeq: normalizedProductSeq,
      }));
    }
  }, [isActive, normalizedProductSeq]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      assetClassification: getAssetClassificationDefault(assetClassificationsList),
      distributionScope: getDistributionScopeDefault(distributionScopeList),
      licensePolicy: getLicensePolicyDefault(licensePolicyList),
      completionRequestDate: getCompletionRequestDaysDefault(clientConfig),
    }));
  }, [assetClassificationsList, distributionScopeList, licensePolicyList, clientConfig]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filteredProjectManagerList = useMemo(() => {
    if (!projectManagerList?.length) return [];
    const selectedMemberIds = new Set(
      (formData.selectedMembers || []).map((member) => member.userId).filter(Boolean),
    );
    return projectManagerList.filter((option) => !selectedMemberIds.has(option.value));
  }, [projectManagerList, formData.selectedMembers]);

  const handleProjectManagerChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      projectManagerId: selectedOption?.value || "",
    }));
  };

  const handlePreviewSelect = (selectedPreview) => {
    setFormData((prev) => ({
      ...prev,
      selectedProjectPreview: selectedPreview
        ? {
            projectPreviewSeq: selectedPreview.projectPreviewSeq,
            projectPreviewName: selectedPreview.projectPreviewName,
          }
        : null,
    }));
  };

  const handleLicenseSelect = (selectedLicense) => {
    setFormData((prev) => ({
      ...prev,
      selectedLicense: selectedLicense
        ? {
            licenseIdentifier: selectedLicense.licenseIdentifier,
            licenseName: selectedLicense.licenseName,
            defaultFlag: selectedLicense.defaultFlag,
          }
        : null,
    }));
  };

  const handleMemberSelect = (selectedMembers) => {
    const members = selectedMembers || [];
    const selectedMemberIds = new Set(members.map((member) => member.userId).filter(Boolean));

    setFormData((prev) => ({
      ...prev,
      selectedMembers: members,
      ...(selectedMemberIds.has(prev.projectManagerId) ? { projectManagerId: "" } : {}),
    }));
  };

  const handleDeleteMember = (member) => {
    const updatedMembers = formData.selectedMembers.filter((m) => m.userId !== member.userId);
    setFormData((prev) => ({
      ...prev,
      selectedMembers: updatedMembers,
    }));
  };

  const validateForm = () => isAddProjectFormValid(formData);

  const isFormValid = () => validateForm();

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    const bodyRequest = buildCreateProjectBody(formData, canAddProjectMemberPermission);
    await createProject.mutateAsync(bodyRequest);
  };

  return {
    assetClassificationsList,
    canAddProjectMemberPermission,
    creatorList,
    creatorOptions,
    departmentList,
    distributionScopeList,
    formData,
    handleDeleteMember,
    handleInputChange,
    handleLicenseSelect,
    handleMemberSelect,
    handlePreviewSelect,
    handleProjectManagerChange,
    handleSave,
    isFormValid,
    isPreviewEnabled,
    isPreviewModalOpen,
    licensePolicyList,
    loading,
    modalSelectLicenseOpen,
    modalSelectMemberOpen,
    productsList,
    projectManagerList: filteredProjectManagerList,
    myProductsList,
    resetFormState,
    setIsPreviewModalOpen,
    setModalSelectLicenseOpen,
    setModalSelectMemberOpen,
  };
};
